import type { TemplateFormat } from "@/config/brandTemplates";

import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Chip,
  Switch,
  addToast,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useDropzone, type FileRejection } from "react-dropzone";
import { Icon } from "@iconify/react";
import DOMPurify from "dompurify";
import { useBrand } from "@webuild-ai/react-auth";

import { useTemplateStore } from "@/stores/TemplateStore";
import {
  verifyBrandDefaultExists,
  formatFileSize,
  MAX_TEMPLATE_SIZE,
} from "@/utils/templateEngine";
import { logger } from "@/telemetry";

interface TemplateUploadZoneProps {
  format: TemplateFormat;
  label: string;
  accept: Record<string, string[]>;
  icon: string;
}

const TemplateUploadZone: React.FC<TemplateUploadZoneProps> = ({ format, label, accept, icon }) => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { getTemplateMetadata, uploadTemplate, removeTemplate, getTemplateData } =
    useTemplateStore();

  const metadata = getTemplateMetadata(format);
  const hasUserOverride = !!metadata;

  // Verify that the brand default template file actually exists on the server
  const [brandTemplateName, setBrandTemplateName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Only check brand default if user hasn't uploaded their own
    if (!hasUserOverride) {
      verifyBrandDefaultExists(format, brand.name).then((name) => {
        if (!cancelled) setBrandTemplateName(name);
      });
    } else {
      setBrandTemplateName(null);
    }

    return () => {
      cancelled = true;
    };
  }, [format, brand.name, hasUserOverride]);

  const [isUploading, setIsUploading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejections: FileRejection[]) => {
      // Handle rejected files
      if (rejections.length > 0) {
        const rejection = rejections[0];
        const errorCode = rejection.errors[0]?.code;

        let message = t("Invalid file type");

        if (errorCode === "file-too-large") {
          message = t("File is too large. Maximum size is 10 MB.");
        } else if (errorCode === "too-many-files") {
          message = t("Only one template file can be uploaded at a time.");
        }

        addToast({
          title: t("Upload failed"),
          description: message,
          color: "danger",
        });

        return;
      }

      const file = acceptedFiles[0];

      if (!file) return;

      // Validate file extension matches the expected format
      const expectedExtensions: Record<TemplateFormat, string[]> = {
        word: [".docx", ".dotx", ".docm", ".dotm"],
        pptx: [".pptx", ".potx", ".pptm", ".potm"],
        xlsx: [".xlsx", ".xltx", ".xlsm", ".xltm"],
      };
      const allowedExts = expectedExtensions[format];
      const expectedExt = allowedExts.join(" / ");

      if (!allowedExts.some((ext) => file.name.toLowerCase().endsWith(ext))) {
        addToast({
          title: t("Upload failed"),
          description: t("Expected a {{ext}} file for this template slot.", { ext: expectedExt }),
          color: "danger",
        });

        return;
      }

      // Validate file size
      if (file.size > MAX_TEMPLATE_SIZE) {
        addToast({
          title: t("Upload failed"),
          description: t("File is too large. Maximum size is 10 MB."),
          color: "danger",
        });

        return;
      }

      setIsUploading(true);

      try {
        await uploadTemplate(format, file);
        addToast({
          title: t("Template uploaded"),
          description: t("{{name}} is now your active {{format}} template.", {
            name: file.name,
            format: format === "word" ? "Word" : format === "pptx" ? "PowerPoint" : "Excel",
          }),
          color: "success",
        });
      } catch (error) {
        logger.error("Template upload failed", { error: String(error) });
        addToast({
          title: t("Upload failed"),
          description: t("Could not save the template. Please try again."),
          color: "danger",
        });
      } finally {
        setIsUploading(false);
      }
    },
    [format, uploadTemplate, t],
  );

  const handleRemove = useCallback(async () => {
    removeTemplate(format);
    setPreviewHtml(null);

    // Check if a brand default exists to show the right message
    const defaultName = await verifyBrandDefaultExists(format, brand.name);

    addToast({
      title: t("Template removed"),
      description: defaultName
        ? t("Exports will use the brand default template.")
        : t("No template active. Upload one or exports will use plain formatting."),
      color: "default",
    });
  }, [format, brand.name, removeTemplate, t]);

  const handlePreview = useCallback(async () => {
    if (format !== "word") return; // Only Word preview supported via mammoth

    setIsLoadingPreview(true);

    try {
      const data = await getTemplateData(format);

      if (!data) {
        addToast({
          title: t("Preview unavailable"),
          description: t("No template data found."),
          color: "warning",
        });

        return;
      }

      const mammoth = await import("mammoth");
      const result = await mammoth.convertToHtml({ arrayBuffer: data });

      setPreviewHtml(result.value);
      setIsPreviewOpen(true);
    } catch (error) {
      logger.error("Template preview failed", { error: String(error) });
      addToast({
        title: t("Preview failed"),
        description: t("Could not generate preview for this template."),
        color: "danger",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  }, [format, getTemplateData, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_TEMPLATE_SIZE,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Icon icon={icon} width={20} />
        <h3 className="font-medium">{label}</h3>
      </div>

      {/* Current template status */}
      {(hasUserOverride || brandTemplateName) && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-default-100 dark:bg-default-50">
          <Icon className="text-success shrink-0" icon="solar:check-circle-bold" width={16} />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-default-700 truncate block">
              {hasUserOverride ? metadata.name : brandTemplateName}
            </span>
            {hasUserOverride && (
              <span className="text-xs text-default-400">
                {formatFileSize(metadata.size)} &middot; {t("User override")}
              </span>
            )}
            {!hasUserOverride && brandTemplateName && (
              <span className="text-xs text-default-400">{t("Brand default")}</span>
            )}
          </div>
          <Chip color={hasUserOverride ? "secondary" : "primary"} size="sm" variant="flat">
            {hasUserOverride ? t("Custom") : t("Default")}
          </Chip>
          {hasUserOverride && format === "word" && (
            <Button
              aria-label={t("Preview template")}
              isLoading={isLoadingPreview}
              size="sm"
              variant="light"
              onPress={handlePreview}
            >
              <Icon icon="solar:eye-bold" width={16} />
            </Button>
          )}
          {hasUserOverride && (
            <Button
              isIconOnly
              aria-label={t("Remove custom template")}
              size="sm"
              variant="light"
              onPress={handleRemove}
            >
              <Icon icon="solar:trash-bin-trash-bold" width={16} />
            </Button>
          )}
        </div>
      )}

      {/* Upload zone */}
      <div
        {...getRootProps()}
        aria-label={t("Upload template file")}
        className={`
          flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed
          cursor-pointer transition-all
          ${
            isDragActive
              ? "border-primary bg-primary-50 dark:bg-primary-900/20"
              : "border-default-300 dark:border-default-600 hover:border-primary hover:bg-default-50 dark:hover:bg-default-100/5"
          }
        `}
        role="button"
      >
        <input {...getInputProps()} />
        {isUploading ? (
          <>
            <Icon className="mb-2 text-primary animate-spin" icon="solar:loading-bold" width={24} />
            <p className="text-sm text-primary">{t("Uploading...")}</p>
          </>
        ) : (
          <>
            <Icon
              className={`mb-2 ${isDragActive ? "text-primary" : "text-default-400"}`}
              icon="solar:upload-bold"
              width={24}
            />
            <p className="text-sm text-default-600">
              {isDragActive
                ? t("Drop template here")
                : hasUserOverride
                  ? t("Drop to replace template")
                  : t("Drop template here or click to browse")}
            </p>
            <p className="text-xs text-default-400 mt-1">
              {format === "word"
                ? ".docx / .dotx / .docm / .dotm"
                : format === "pptx"
                  ? ".pptx / .potx / .pptm / .potm"
                  : ".xlsx / .xltx / .xlsm / .xltm"}{" "}
              &middot; {t("Max 10 MB")}
            </p>
          </>
        )}
      </div>

      {/* Word template preview modal */}
      {format === "word" && (
        <Modal
          isOpen={isPreviewOpen}
          scrollBehavior="inside"
          size="3xl"
          onClose={() => setIsPreviewOpen(false)}
        >
          <ModalContent>
            <ModalHeader>
              <div className="flex items-center gap-2">
                <Icon icon="vscode-icons:file-type-word" width={20} />
                <span>{t("Template Preview")}</span>
              </div>
            </ModalHeader>
            <ModalBody>
              {previewHtml ? (
                <div
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
                  className="prose dark:prose-invert max-w-none p-4 border rounded-lg bg-content1"
                />
              ) : (
                <p className="text-default-500 text-center py-8">{t("No preview available")}</p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setIsPreviewOpen(false)}>
                {t("Close")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export const TemplateSettings: React.FC = () => {
  const { t } = useTranslation();
  const { useTemplateForExports, toggleUseTemplateForExports } = useTemplateStore();

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Icon icon="solar:document-bold" width={24} />
          <span>{t("Document Templates")}</span>
        </h2>
      </CardHeader>
      <CardBody className="space-y-6">
        {/* Enable/Disable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">{t("Use Templates for Exports")}</h3>
            <p className="text-sm text-default-500">
              {t("Apply branded templates when exporting to Word or PowerPoint")}
            </p>
          </div>
          <Switch
            aria-label={t("Use Templates for Exports")}
            data-testid="template-exports-switch"
            isSelected={useTemplateForExports}
            onValueChange={toggleUseTemplateForExports}
          />
        </div>

        {useTemplateForExports && (
          <>
            {/* Word Template */}
            <TemplateUploadZone
              accept={{
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
                  ".docx",
                ],
                "application/vnd.openxmlformats-officedocument.wordprocessingml.template": [
                  ".dotx",
                ],
                "application/vnd.ms-word.document.macroEnabled.12": [".docm"],
                "application/vnd.ms-word.template.macroEnabled.12": [".dotm"],
              }}
              format="word"
              icon="vscode-icons:file-type-word"
              label={t("Word Template")}
            />

            {/* PowerPoint Template */}
            <TemplateUploadZone
              accept={{
                "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
                  ".pptx",
                ],
                "application/vnd.openxmlformats-officedocument.presentationml.template": [".potx"],
                "application/vnd.ms-powerpoint.presentation.macroEnabled.12": [".pptm"],
                "application/vnd.ms-powerpoint.template.macroEnabled.12": [".potm"],
              }}
              format="pptx"
              icon="vscode-icons:file-type-powerpoint"
              label={t("PowerPoint Template")}
            />

            {/* Excel Template */}
            <TemplateUploadZone
              accept={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [".xltx"],
                "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
                "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
              }}
              format="xlsx"
              icon="vscode-icons:file-type-excel"
              label={t("Excel Template")}
            />

            {/* Info */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-default-100 dark:bg-default-50">
              <Icon
                className="text-default-500 shrink-0 mt-0.5"
                icon="solar:info-circle-bold"
                width={16}
              />
              <p className="text-xs text-default-500">
                {t(
                  "Templates define the visual style (cover page, headers, footers, colours) for exported documents. Upload your own or use the brand default. Use placeholders like {{TITLE}}, {{DATE}}, {{AUTHOR}} in your templates.",
                )}
              </p>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};
